import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal, Label } from "./Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.2em] text-[#8b7e76]">{label}</span>
    <input
      {...props}
      className="mt-2 w-full bg-transparent border-b border-[#c9bfae] pb-2 text-[#2c2826] placeholder:text-[#b6ab9c] focus:border-[#985F47] outline-none transition-colors duration-300"
    />
  </label>
);

export const Enquiry = ({ prefill }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", giftNote: "", recipientAddress: "", deliveryDate: "" });
  const [sending, setSending] = useState(false);
  const isHamper = prefill?.kind === "hamper";

  useEffect(() => {
    if (isHamper) {
      const list = prefill.items?.join(", ");
      setForm((f) => ({
        ...f,
        message: `I'd like a custom ${prefill.occasion} hamper with: ${list}.`,
      }));
      document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [prefill, isHamper]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiry`, {
        ...form,
        gift_note: form.giftNote,
        recipient_address: form.recipientAddress,
        delivery_date: form.deliveryDate,
        kind: prefill?.kind || "general",
        occasion: prefill?.occasion || null,
        items: prefill?.items || [],
      });
      toast.success("Thank you — your enquiry is on its way. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "", giftNote: "", recipientAddress: "", deliveryDate: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquire" data-testid="enquiry" className="relative py-24 md:py-36 bg-[#EAE5DC]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
        <div>
          <Reveal><Label>Enquire &amp; Collaborate</Label></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2c2826] leading-[1.02]">
              Let&rsquo;s make<br /><span className="italic text-[#985F47]">something warm.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md text-[#5a5451] leading-relaxed">
              Whether it&rsquo;s a single piece, a custom hamper or corporate gifting at scale —
              tell us what you have in mind and we&rsquo;ll craft it for you.
            </p>
          </Reveal>
          {isHamper && (
            <Reveal delay={0.15}>
              <div className="mt-8 border border-[#c9bfae] bg-[#F7F5F0] p-5">
                <Label>Your hamper draft</Label>
                <p className="mt-2 font-serif text-2xl text-[#2c2826]">{prefill.occasion}</p>
                <p className="mt-1 text-sm text-[#5a5451]">{prefill.items?.join(" · ")}</p>
              </div>
            </Reveal>
          )}
        </div>

        <Reveal delay={0.1}>
          <motion.form data-testid="enquiry-form" onSubmit={submit} className="space-y-7 bg-[#F7F5F0] p-7 md:p-10 border border-[#ddd4c7]">
            <Field label="Full name" data-testid="enquiry-name" value={form.name} onChange={set("name")} placeholder="Your name" />
            <div className="grid sm:grid-cols-2 gap-7">
              <Field label="Email" data-testid="enquiry-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" />
              <Field label="Phone" data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} placeholder="Optional" />
            </div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#8b7e76]">Message</span>
              <textarea
                data-testid="enquiry-message" value={form.message} onChange={set("message")} rows={4}
                placeholder="Tell us what you're looking for..."
                className="mt-2 w-full bg-transparent border-b border-[#c9bfae] pb-2 text-[#2c2826] placeholder:text-[#b6ab9c] focus:border-[#985F47] outline-none transition-colors duration-300 resize-none"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#8b7e76]">Gift note <span className="normal-case tracking-normal text-[#b6ab9c]">(optional — we'll add a handwritten card)</span></span>
              <textarea
                data-testid="enquiry-giftnote" value={form.giftNote} onChange={set("giftNote")} rows={2}
                placeholder="e.g. Happy Diwali, with love — Aisha"
                className="mt-2 w-full bg-transparent border-b border-[#c9bfae] pb-2 text-[#2c2826] placeholder:text-[#b6ab9c] focus:border-[#985F47] outline-none transition-colors duration-300 resize-none"
              />
            </label>
            <div className="grid sm:grid-cols-2 gap-7">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#8b7e76]">Deliver to <span className="normal-case tracking-normal text-[#b6ab9c]">(optional)</span></span>
                <textarea
                  data-testid="enquiry-address" value={form.recipientAddress} onChange={set("recipientAddress")} rows={2}
                  placeholder="Recipient name & address"
                  className="mt-2 w-full bg-transparent border-b border-[#c9bfae] pb-2 text-[#2c2826] placeholder:text-[#b6ab9c] focus:border-[#985F47] outline-none transition-colors duration-300 resize-none"
                />
              </label>
              <Field label="Preferred delivery date" data-testid="enquiry-date" type="date" value={form.deliveryDate} onChange={set("deliveryDate")} />
            </div>
            <button
              data-testid="enquiry-submit" type="submit" disabled={sending}
              className="w-full rounded-full bg-[#2c2826] text-[#F7F5F0] py-4 text-sm hover:bg-[#985F47] transition-colors duration-300 disabled:opacity-60"
            >
              {sending ? "Sending..." : "Send enquiry"}
            </button>
          </motion.form>
        </Reveal>
      </div>
    </section>
  );
};
